import React from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { MessageCircle, Copy, Info } from 'lucide-react';

// Single source-of-truth for every system-triggered template.
// Body text is the EXACT string to paste into the Eden CRM template creation UI.
// If a Meta template is later renamed, update `name` here to match; the sender
// picks up the new name automatically (no code change beyond this file).
export const EVENT_TEMPLATES = [
  {
    name: 'eden_followup_confirmed',
    event: 'Fires when a follow-up appointment is booked for a TIME-SLOT service (has a scheduled_time).',
    fireRule: 'Backend hook: PUT /api/appointments/{id} when follow_up_appointment_id is set. Router picks this template if service_type != "queue_based" and scheduled_time is present.',
    vars: '{{1}} Salutation+Name · {{2}} Date · {{3}} Time · {{4}} Service',
    body: `Hi {{1}}, your follow-up appointment has been booked for {{2}} at {{3}} for {{4}}.

We look forward to seeing you at Eden Skin Clinic!`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Mr. Ravi', '25 Aug 2026', '10:30 AM', 'Consultation'],
  },
  {
    name: 'eden_followup_confirmed_queue',
    event: 'Fires when a follow-up appointment is booked for a QUEUE-BASED service (no scheduled_time — patient walks in that day).',
    fireRule: 'Backend hook: PUT /api/appointments/{id} when follow_up_appointment_id is set. Router picks this template if service_type == "queue_based" OR scheduled_time is missing/"queue".',
    vars: '{{1}} Salutation+Name · {{2}} Date · {{3}} Service',
    body: `Hi {{1}}, your follow-up appointment has been booked for {{2}} for {{3}}.

Since this is a walk-in service, please arrive any time during clinic hours on that day.

We look forward to seeing you at Eden Skin Clinic!`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Ms. Priya', '25 Aug 2026', 'HydraFacial'],
  },
  {
    name: 'eden_payment_received',
    event: 'Fires on every completed payment > ₹0 (both service & package payments).',
    fireRule: 'Backend hook: POST /api/payments — immediately after payments.insert_one when status=COMPLETED.',
    vars: '{{1}} Salutation+Name · {{2}} Amount (₹)',
    body: `Dear {{1}}, we have received your payment of ₹{{2}}.

Thank you for choosing Eden Skin Clinic!`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Ms. Priya', '2500'],
  },
  {
    name: 'eden_package_activated',
    event: 'Fires when a new package is sold to a patient.',
    fireRule: 'Backend hook: POST /api/packages — after package insert.',
    vars: '{{1}} Salutation+Name · {{2}} Package Ref · {{3}} Services (comma-separated, "Name x qty")',
    body: `Hi {{1}}, your package {{2}} has been activated with these services: {{3}}.

Enjoy your visits at Eden Skin Clinic!`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Mr. Ravi', 'PKG-0227', 'Consultation x 3, HydraFacial x 2'],
  },
  {
    name: 'eden_package_visit_logged',
    event: 'Fires when a session/credit is redeemed from an active package.',
    fireRule: 'Backend hook: POST /api/packages/{id}/consume — after recompute.',
    vars: '{{1}} Salutation+Name · {{2}} Service · {{3}} Package Ref · {{4}} Sessions Remaining',
    body: `Hi {{1}}, {{2}} has been redeemed from your package {{3}}. Sessions remaining: {{4}}.

Thank you for visiting Eden Skin Clinic!`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Ms. Priya', 'HydraFacial', 'PKG-0227', '3'],
  },
  {
    name: 'eden_reward_points_earned',
    event: 'Fires right after any payment that earned reward points (points_earned > 0).',
    fireRule: 'Backend hook: POST /api/payments AND package payment writer (_write_package_payment). Dispatched after award_points_for_payment returns a batch. Skipped when points_earned is 0.',
    vars: '{{1}} Salutation+Name · {{2}} Points earned this transaction · {{3}} Total available points',
    body: `Hi {{1}}, you have earned {{2}} reward points on your recent transaction.
Your total available balance is now {{3}} points.
Tap the button below to view your rewards, and the details of unlocked and locked rewards.
Eden Skin Clinic reserves all rights.`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Mr. Ravi', '100', '640'],
    interactiveButtons: {
      title: '1 URL button — dynamic (per-patient signed token)',
      note: "Add ONE URL button labeled 'View my Rewards'. Type: Dynamic URL. Configure the URL as https://<your-domain>/rewards/wallet/{{1}} where {{1}} is the button variable. Backend sends the signed JWT token (JWT_SECRET, kind='reward_wallet', 60-day exp) via button_url_variables={'0': <token>}. Example URL to preview: https://clinic-flow-12.emergent.host/rewards/wallet/eyJhbGciOi... (page loads from GET /api/rewards/wallet-by-token/<token>).",
      buttons: ['View my Rewards (dynamic URL button)'],
    },
  },
  {
    name: 'eden_reward_points_redeemed',
    event: 'Fires when a patient redeems reward points against a reward (free service).',
    fireRule: 'Backend hook: POST /api/rewards/redeem — after successful FIFO deduction.',
    vars: '{{1}} Salutation+Name · {{2}} Points redeemed · {{3}} Reward name · {{4}} Remaining points',
    body: `Hi {{1}}, you have successfully redeemed {{2}} reward points for {{3}}. Your remaining balance is {{4}} points.

We look forward to seeing you at Eden Skin Clinic!`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Ms. Priya', '500', 'Complimentary Consultation', '140'],
  },
  {
    name: 'eden_feedback_ask_ameesha',
    event: 'FIRST step of the feedback flow — fires the moment a "SKIN CONSULT DR AMEESHA" job card is CLOSED / patient checks out. Patient rates via 2 URL BUTTONS (Meta allows max 2 URL buttons).',
    fireRule: 'Backend hook: check-out / JC-close, when service_name matches AMEESHA_SERVICE_NAME. Task inserted with status="feedback_asked". Repeatable on every checkout (24h cooldown). ⭐5 → reward-task cascade; 4-or-less → apology + admin alert.',
    vars: '{{1}} Salutation+Name',
    body: `Dear {{1}},
Thank you for visiting Dr Ameesha today. We hope you left feeling well cared for.
How would you rate your experience? Tap a button below.`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Ms. Priya'],
    interactiveButtons: {
      title: '2 DYNAMIC URL buttons — patient taps a rating link',
      note: "Author 2 buttons of type Dynamic URL. Button 1 '⭐ 5 - Excellent' → https://<your-domain>/feedback/5/{{1}} · Button 2 '4 or less' → https://<your-domain>/feedback/4/{{1}}. Backend sends the SAME signed token for both (button_url_variables={'0','1'}). The page auto-records the score: 5 → the next reward-task template (eden_task_generic) is sent; 4 or less → apology template + admin alert.",
      buttons: ['⭐ 5 - Excellent (URL → /feedback/5/{{token}})', '4 or less (URL → /feedback/4/{{token}})'],
    },
  },
  {
    name: 'eden_feedback_ask_eden',
    event: 'FIRST step for every service OTHER than SKIN CONSULT DR AMEESHA — fires at 8 PM, 3 days after checkout, via the daily 20:00 IST cron (no messages go out at midnight). Same 2 URL rating buttons.',
    fireRule: 'Backend cron: daily Eden feedback batch — task inserted with status="feedback_asked". Repeatable (24h cooldown). ⭐5 → reward-task cascade; 4-or-less → apology + admin alert.',
    vars: '{{1}} Salutation+Name',
    body: `Dear {{1}},
Thank you for visiting Eden Skin Clinic. We hope you left feeling well cared for.
How would you rate your experience? Tap a button below.`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Mr. Ravi'],
    interactiveButtons: {
      title: '2 DYNAMIC URL buttons — patient taps a rating link',
      note: "Identical button setup to eden_feedback_ask_ameesha: 2 Dynamic URL buttons → /feedback/5/{{1}} and /feedback/4/{{1}}. Same token in both.",
      buttons: ['⭐ 5 - Excellent (URL → /feedback/5/{{token}})', '4 or less (URL → /feedback/4/{{token}})'],
    },
  },
  {
    name: 'eden_feedback_thankyou_ameesha',
    event: 'Sent to patients who rated 3 or less on the Dr. Ameesha feedback ask. Callback number is hard-coded in the body.',
    fireRule: 'Backend: POST /api/public/feedback-rate — sent immediately after the "4 or less" URL button is clicked. Also fires eden_admin_low_feedback to admin phone.',
    vars: '{{1}} Salutation+Name',
    body: `Dear {{1}},
Thank you for the honest feedback — we're sorry your experience with Dr Ameesha wasn't better.
Our manager will reach out shortly to make things right. If it's easier, please feel free to call us directly at +91 77101 17777.
We'd love the chance to earn your trust back.`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Ms. Priya'],
  },
  {
    name: 'eden_feedback_thankyou_eden',
    event: 'Sent to patients who rated 3 or less on the Eden feedback ask. Callback number is hard-coded in the body.',
    fireRule: 'Backend: POST /api/public/feedback-rate — sent immediately after the "4 or less" URL button is clicked. Also fires eden_admin_low_feedback.',
    vars: '{{1}} Salutation+Name',
    body: `Dear {{1}},
Thank you for the honest feedback — we're sorry your experience at Eden Skin Clinic wasn't better.
Our manager will reach out shortly to make things right. If it's easier, please feel free to call us directly at +91 77101 17777.
We'd love the chance to earn your trust back.`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Mr. Ravi'],
  },
  {
    name: 'eden_admin_low_feedback',
    event: 'Fires to the admin phone (ADMIN_REPORT_WA_PHONE) whenever a patient rates 3 or less on the internal feedback ask.',
    fireRule: 'Backend: POST /api/public/feedback-rate — fired in parallel with the patient apology template when the "4 or less" URL is clicked.',
    vars: '{{1}} Patient name · {{2}} Patient phone · {{3}} Service · {{4}} Appointment ref · {{5}} Score (e.g. "3 / 5")',
    body: `Low feedback alert
Patient: {{1}} ({{2}})
Service: {{3}}
Appointment: {{4}}
Score: {{5}}
Please have the manager reach out.`,
    footer: 'Eden Skin Clinic — Ops',
    examples: ['Priya Sharma', '+91 98765 43210', 'SKIN CONSULT DR AMEESHA', 'APT-2026-0117', '3 / 5'],
  },
  {
    name: 'eden_task_generic',
    event: 'REWARD TASK CASCADE — ONE shared template for ALL reward tasks. Fires right after a patient taps the ⭐5 rating button on the feedback ask. The backend picks the first incomplete task (Patient Feedback → Reward Tasks) and injects that task\'s configured message into {{2}}.',
    fireRule: 'Backend: POST /api/public/feedback-rate (score 5) → _send_next_reward_task → always sends eden_task_generic. {{2}} = the "Task message" configured on each task (include the task link, e.g. your Google review URL, inside it). A task counts as DONE once the reviewer approves OR rejects its screenshot.',
    vars: '{{1}} Salutation+Name · {{2}} Full task message (per-task, single line — include the task link in it) · {{3}} Coupon ₹ amount (per-task; differs for Ameesha vs Eden feedback)',
    body: `Dear {{1}},

{{2}}

A discount coupon of ₹{{3}} will be added to your account once your submission is verified.

Regards,
Eden Skin Clinic`,
    footer: '',
    examples: ['Ms. Priya', 'Thank you for the kind words! Leave us a Google review at https://maps.app.goo.gl/f9cVMF93ZNDvsV1F9 and tap Upload screenshot below once posted.', '300'],
    interactiveButtons: {
      title: '1 DYNAMIC URL button (screenshot upload)',
      note: "One Dynamic URL button: 'Upload screenshot' → https://<your-domain>/task-upload/{{1}} — backend supplies the signed token via button_url_variables={'0': token}. The task's own link (Google review / blog etc.) lives inside the {{2}} body variable, so ONE template serves every task in the cascade.",
      buttons: ['Upload screenshot (dynamic URL → /task-upload/{{token}})'],
    },
  },
  {
    name: 'eden_payment_receipt',
    event: 'PAYMENT RECEIPT — ONE template for every receipt kind (regular payment, package purchase, package balance, ₹0 package redemption). Sent when Teller/Admin taps "Send PDF on WhatsApp" on a receipt. The PDF is delivered as a document attachment in the same message.',
    fireRule: 'Backend: POST /api/payments/{id}/send-receipt → send_wa_payment_receipt. Backend passes header_document = {link: https://<domain>/api/public/receipt/{token}, filename: Receipt-<no>.pdf}; the Eden CRM bridge forwards it to Meta as the DOCUMENT header parameter. Link valid 365 days (env RECEIPT_LINK_VALID_DAYS). Logged in wa_send_logs as event payment_receipt.',
    vars: '{{1}} Salutation+Name · {{2}} Receipt number',
    body: `Dear {{1}},

Thank you for your visit to Eden Skin & Wellness Clinic.

Your payment receipt {{2}} is attached with this message. Please keep it for your records.

Regards,
Eden Skin & Wellness Clinic`,
    footer: 'Eden Skin Clinic',
    examples: ['Ms. Priya', 'RCP-20261004-0005'],
    interactiveButtons: {
      title: 'HEADER = Media → Document (PDF). No buttons.',
      note: "Author with Header type Media → Document and upload any sample PDF (Meta only needs a sample for review). Category: Utility. No buttons required. Note: WhatsApp always renders the document header ABOVE the body text — the patient sees the PDF card first, then the text. CRM bridge requirement: forward header_document {link, filename} as components[HEADER].parameters[{type:'document', document:{link, filename}}].",
      buttons: [],
    },
  },
  {
    name: 'eden_patient_register',
    event: 'NEW CONTACT SELF-REGISTRATION — Front Desk taps "Send registration link on WhatsApp" when a phone number has no patient record (Create Appointment → No patient found). The patient fills name, gender, DOB/age (+ optional email/address) on a public form; they then appear in the dashboard "New Registrations" box for one-click booking.',
    fireRule: 'Backend: POST /api/registrations/invite → send_whatsapp_template(button_url_variables={"0": token}). Link valid 7 days. Logged in wa_send_logs as event registration_invite. Public form: GET/POST /api/public/register/{token}.',
    vars: 'No body variables. One Dynamic URL button → https://<your-domain>/register/{{1}}',
    body: `Welcome to Eden Skin & Wellness Clinic! 🌿

We're delighted to have you with us. To make your visit smooth and quick, please take a minute to register using the link below.

See you soon!`,
    footer: 'Eden Skin Clinic',
    examples: [],
    interactiveButtons: {
      title: 'ONE Dynamic URL button. Category: Utility.',
      note: "Button text 'Register now' → URL https://<your-domain>/register/{{1}} (Dynamic). Backend supplies the signed token via button_url_variables={'0': token}. No header, no body variables — the name isn't known yet.",
      buttons: ['Register now (dynamic URL → /register/{{token}})'],
    },
  },
  {
    name: 'eden_coupon_issued',
    event: 'Fires to the patient the moment an admin or feedback reviewer approves their screenshot in the Patient Feedback section and a coupon is minted.',
    fireRule: 'Backend: POST /api/feedback/events/{id}/approve → _issue_coupon_for_task — right after inserting the patient_coupons row.',
    vars: '{{1}} Salutation+Name · {{2}} Coupon code · {{3}} Expiry date (YYYY-MM-DD)',
    body: `Thank you {{1}}!
Your discount coupon is ready: {{2}}
Valid until {{3}} — applicable on all services at Eden Skin Clinic.
See you at your next visit!`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Ms. Priya', 'EDEN-A7XK9Q', '2026-11-05'],
  },
  {
    name: 'eden_admin_jobcard_opened',
    event: 'Fires when a job card is opened at patient check-in.',
    fireRule: 'Backend hook: POST /api/appointments/{id}/check-in — right after job_cards.insert_one. Sent to ADMIN_REPORT_WA_PHONE only.',
    vars: '{{1}} Patient · {{2}} Service · {{3}} Payment status (Paid / Unpaid / Paid via package) · {{4}} Amount (₹) · {{5}} Package info line',
    body: `Job Card OPENED at Eden Skin Clinic

Patient: {{1}}
Service: {{2}}
Payment: {{3}} · ₹{{4}}
{{5}}`,
    footer: 'Eden Skin Clinic — Ops',
    examples: ['Rajesh Kumar', 'HydraFacial', 'Paid via package', '0', 'Package: PKG-0227'],
  },
  {
    name: 'eden_admin_jobcard_closed',
    event: 'Fires when a job card is closed (status → completed).',
    fireRule: 'Backend hook: PUT /api/job-cards/{id} — right after the status transitions to COMPLETED. Sent to ADMIN_REPORT_WA_PHONE only.',
    vars: '{{1}} Patient · {{2}} Service · {{3}} Payment status · {{4}} Amount (₹) · {{5}} Package info line',
    body: `Job Card CLOSED at Eden Skin Clinic

Patient: {{1}}
Service: {{2}}
Payment: {{3}} · ₹{{4}}
{{5}}`,
    footer: 'Eden Skin Clinic — Ops',
    examples: ['Priya Sharma', 'Consultation', 'Paid', '2500', 'Walk-in / No package'],
  },
  {
    name: 'eden_admin_daily_summary',
    event: 'Fires on EVERY completed payment — a live day-summary to the admin phone (ADMIN_REPORT_WA_PHONE).',
    fireRule: 'Backend hook: POST /api/payments — right after the patient payment_received message.',
    vars: '{{1}} Total ₹ · {{2}} Method breakdown · {{3}} Per-counsellor breakdown (single line, counsellors joined with " | " · per-method splits with " · ") · {{4}} Packages block · {{5}} Job Cards block',
    body: `Eden Skin Clinic — Day Summary

Total Collection: ₹{{1}}
Payment Methods: {{2}}
Counsellor Collections: {{3}}
Packages: {{4}}
Job Cards: {{5}}`,
    footer: 'Team Eden Skin Clinic',
    examples: ['45250', 'Cash ₹15000 · UPI ₹20250 · Card ₹10000', 'Dr. Anita [₹22000]: Cash ₹5000 · UPI ₹15000 · Card ₹2000 | Dr. Vikas [₹23250]: UPI ₹18000 · Card ₹5250', 'Created 3 · Updated 2 · Redeemed 1', 'Open 5 · Closed 8'],
  },
  {
    name: 'eden_appointment_reminder',
    event: 'Timed appointment reminder — sent at 6 PM IST the day before a time-slot appointment.',
    fireRule: 'Backend cron: send_24h_appointment_reminders (20:00 IST run covers tomorrow\'s bookings) or manual "Send Reminder" — for time-slot bookings only.',
    vars: '{{1}} Name · {{2}} Date · {{3}} Time · {{4}} Service',
    body: `Hi {{1}}, this is a reminder of your appointment at Eden Skin Clinic tomorrow, {{2}} at {{3}} for {{4}}.

Please tap the button below to confirm your visit. For anything else, our front desk is a call away.

We look forward to seeing you!`,
    footer: 'Team Eden Skin Clinic',
    examples: ['Ravi', '25 Aug 2026', '10:30 AM', 'Consultation'],
    interactiveButtons: {
      title: '1 DYNAMIC URL button (confirm) + 1 PHONE button (front desk)',
      note: "Button 1 (Dynamic URL): '✅ Confirm appointment' → https://<your-domain>/confirm/{{1}} — the backend fills a signed token; the page auto-confirms the appointment (status → confirmed, reminder marked responded). Button 2 (Phone number): '📞 Contact front desk' → +91 7710117777 — dials directly, no webhook needed. Remove the old quick-reply buttons.",
      buttons: ['✅ Confirm appointment (dynamic URL → /confirm/{{token}})', '📞 Contact front desk (phone: +91 7710117777)'],
    },
  },
  {
    name: 'eden_appointment_queue',
    event: 'Same reminder path but for queue-based (no fixed time) appointments — sent at 6 PM IST the day before.',
    fireRule: 'Backend cron: send_24h_appointment_reminders — when service_type=queue_based.',
    vars: '{{1}} Name · {{2}} Date · {{3}} Service',
    body: `Hi {{1}}, this is a reminder of your visit to Eden Skin Clinic tomorrow, {{2}} for {{3}}.

Please tap the button below to confirm your visit. For anything else, our front desk is a call away.

We look forward to seeing you!`,
    footer: 'Eden Skin Clinic',
    examples: ['Ravi', '25 Aug 2026', 'Consultation'],
    interactiveButtons: {
      title: '1 DYNAMIC URL button (confirm) + 1 PHONE button (front desk)',
      note: "Same buttons as eden_appointment_reminder: Dynamic URL '✅ Confirm appointment' → https://<your-domain>/confirm/{{1}} and Phone '📞 Contact front desk' → +91 7710117777.",
      buttons: ['✅ Confirm appointment (dynamic URL → /confirm/{{token}})', '📞 Contact front desk (phone: +91 7710117777)'],
    },
  },
  {
    name: 'eden_appointment_missed',
    event: 'Follow-up nudge when a patient misses their appointment — Day 0 / Day 7 / Day 21 / Day 45.',
    fireRule: 'Backend hook: no-show detection job. **Skipped entirely** (all days) if the patient already has a completed job card for the same service in the past 7 days — detected via job card closure. Also skipped once the patient rebooks any other appointment.',
    vars: '{{1}} Name · {{2}} Date · {{3}} Service',
    body: `Hi {{1}}, we noticed you missed your appointment on {{2}} for {{3}}.

We'd love to help you reschedule.

Please call us at +917710117777 to rebook.`,
    footer: 'Eden Skin Clinic',
    examples: ['Ravi', '25 Aug 2026', 'Consultation'],
  },
  {
    name: 'eden_admin_daily_wa_report',
    event: 'DAILY ADMIN DIGEST #1 — fires to the admin phone every day at 8:00 PM IST with the day\'s WhatsApp template send counts.',
    fireRule: 'Backend cron: send_admin_daily_reports at 20:00 IST → sent to ADMIN_REPORT_WA_PHONE. Failing templates are listed first in the breakdown.',
    vars: '{{1}} Date · {{2}} Total sent · {{3}} Total failed · {{4}} Per-template breakdown (single line, semicolon-separated)',
    body: `Eden Skin Clinic - WhatsApp report for {{1}}
Messages sent: {{2}}
Failed: {{3}}
Breakdown: {{4}}`,
    footer: 'Eden Skin Clinic — Ops',
    examples: ['27 Aug 2026', '42', '2', 'eden_task_google_review: 0 sent, 2 FAILED; eden_appointment_reminder: 18 sent; eden_coupon_issued: 3 sent'],
  },
  {
    name: 'eden_admin_daily_appointments',
    event: 'DAILY ADMIN DIGEST #2 — fires to the admin phone every day at 8:00 PM IST with the day\'s appointment activity stats.',
    fireRule: 'Backend cron: send_admin_daily_reports at 20:00 IST → sent to ADMIN_REPORT_WA_PHONE right after the WA report message.',
    vars: '{{1}} Date · {{2}} Check-ins · {{3}} Check-outs · {{4}} Follow-ups created · {{5}} Cancelled · {{6}} No-shows · {{7}} Rescheduled · {{8}} Forwarded',
    body: `Eden Skin Clinic - Appointment summary for {{1}}
Check-ins: {{2}}
Check-outs: {{3}}
Follow-ups created: {{4}}
Cancelled: {{5}}
No-shows: {{6}}
Rescheduled: {{7}}
Forwarded: {{8}}`,
    footer: 'Eden Skin Clinic — Ops',
    examples: ['27 Aug 2026', '14', '12', '5', '2', '1', '3', '3'],
  },
];

const copy = (text) => {
  try { navigator.clipboard.writeText(text); toast.success('Copied'); } catch { toast.error('Copy failed'); }
};

export default function WhatsAppTemplateGuidePage({ user, onLogout }) {
  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="wa-guide-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
            <Info className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WhatsApp Template Guide</h1>
            <p className="text-sm text-gray-500">When each message fires · exact bodies to submit in the Eden CRM</p>
          </div>
        </div>

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex items-start gap-2">
          <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold mb-1">How to add or edit a template</div>
            <ol className="list-decimal ml-4 space-y-0.5">
              <li>Copy the body below with the &ldquo;Copy body&rdquo; button.</li>
              <li>Open Eden CRM → Templates → New. Paste the body, set language to <b>en_US</b>, category <b>UTILITY</b>, and add the footer shown.</li>
              <li>Meta approval typically takes ~15 min. Once <b>APPROVED</b>, the app auto-fires on the next matching event — no redeploy needed.</li>
              <li>Manage the live list, delivery status and failures on the <b>WhatsApp Templates</b> page.</li>
            </ol>
          </div>
        </div>

        <div className="space-y-3">
          {EVENT_TEMPLATES.map((et) => (
            <div key={et.name} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`guide-tpl-${et.name}`}>
              <div className="bg-slate-50 border-b border-slate-100 px-4 py-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm text-slate-900">{et.name}</span>
                  <button onClick={() => copy(et.name)} className="text-slate-400 hover:text-slate-700" title="Copy name"><Copy className="w-3 h-3" /></button>
                </div>
                <div className="text-xs text-slate-700 mt-1">{et.event}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{et.fireRule}</div>
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Template body</div>
                  <button
                    onClick={() => copy(et.body)}
                    className="text-[11px] font-medium text-teal-700 hover:text-teal-900 flex items-center gap-1"
                    data-testid={`copy-body-${et.name}`}
                  >
                    <Copy className="w-3 h-3" /> Copy body
                  </button>
                </div>
                <pre className="text-[12px] text-slate-800 whitespace-pre-wrap font-sans bg-slate-50 rounded-lg p-3 border border-slate-100 leading-snug">{et.body}</pre>
                <div className="mt-2 text-[10px] text-slate-500 space-y-0.5">
                  <div><span className="font-semibold text-slate-600">Footer:</span> {et.footer}</div>
                  <div><span className="font-semibold text-slate-600">Language:</span> en_US · <span className="font-semibold text-slate-600">Category:</span> UTILITY</div>
                  <div><span className="font-semibold text-slate-600">Variables:</span> {et.vars}</div>
                  <div><span className="font-semibold text-slate-600">Sample values:</span> {et.examples.join(' | ')}</div>
                </div>

                {et.interactiveButtons && (
                  <div className="mt-3 rounded-lg border border-purple-200 bg-purple-50 p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-purple-800 mb-1">
                      {et.interactiveButtons.title || 'Interactive buttons'}
                    </div>
                    <div className="text-[11.5px] text-purple-900 leading-relaxed">{et.interactiveButtons.note}</div>
                    {Array.isArray(et.interactiveButtons.buttons) && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {et.interactiveButtons.buttons.map(b => (
                          <span
                            key={b}
                            className="inline-flex items-center text-[11px] font-medium text-purple-900 bg-white border border-purple-300 px-2 py-0.5 rounded-full"
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
